import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Select, Tooltip } from '../../components';
import * as partnerService from '../../services/partner-service';
import type { FounderRow } from '../../services/partner-service';
import * as associateService from '../../services/associate-service';
import type {
  MentorRow,
  InvestorRow,
  PartnerDoc,
} from '../../services/associate-service';

// ──────────────────────────────────────────────────────────────────────────
// Shared styling helpers
// ──────────────────────────────────────────────────────────────────────────

const industryColor: Record<string, string> = {
  Technology: 'bg-chart/10 text-chart border-chart/20',
  HealthTech: 'bg-success/10 text-success border-success/20',
  EdTech: 'bg-role-admin/10 text-role-admin border-role-admin/20',
  Logistics: 'bg-warning/10 text-warning border-warning/20',
  Finance: 'bg-warning/10 text-warning border-warning/20',
  FinTech: 'bg-warning/10 text-warning border-warning/20',
  CleanTech: 'bg-success/10 text-success border-success/20',
  AgriTech: 'bg-success/10 text-success border-success/20',
  SaaS: 'bg-chart/10 text-chart border-chart/20',
  Infrastructure: 'bg-role-admin/10 text-role-admin border-role-admin/20',
  'E-commerce': 'bg-warning/10 text-warning border-warning/20',
};

const stageLabel: Record<string, string> = {
  idea: 'Idea',
  mvp: 'MVP',
  growth: 'Growth',
  scale: 'Scale',
};

const pitchBadge = (status: FounderRow['pitchStatus']): string => {
  switch (status) {
    case 'approved':
      return 'bg-success/15 text-success border-success/30';
    case 'published':
      return 'bg-chart/15 text-chart border-chart/30';
    case 'submitted':
      return 'bg-warning/15 text-warning border-warning/30';
    case 'draft':
      return 'bg-surface text-muted border-line';
    default:
      return 'bg-surface text-muted border-line';
  }
};

const pitchLabel: Record<FounderRow['pitchStatus'], string> = {
  none: '—',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  published: 'Published',
};

const partnerTypeColor: Record<PartnerDoc['type'], string> = {
  University: 'bg-chart/10 text-chart border-chart/20',
  Accelerator: 'bg-success/10 text-success border-success/20',
  Government: 'bg-warning/10 text-warning border-warning/20',
  Corporate: 'bg-role-admin/10 text-role-admin border-role-admin/20',
};

const attendanceDot = (attended: number, invited: number): string => {
  if (invited === 0) return 'bg-muted';
  const ratio = attended / invited;
  if (ratio >= 0.8) return 'bg-success';
  if (ratio >= 0.5) return 'bg-warning';
  return 'bg-danger';
};

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ──────────────────────────────────────────────────────────────────────────
// Tab constants
// ──────────────────────────────────────────────────────────────────────────

type TabId = 'startups' | 'mentors' | 'partners' | 'investors';
const VALID_TABS: TabId[] = ['startups', 'mentors', 'partners', 'investors'];

const SearchIcon: React.FC = () => (
  <svg className="w-4 h-4 text-muted pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M16.5 10.5a6 6 0 11-12 0 6 6 0 0112 0z" />
  </svg>
);

const ChevronRight: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

const AssociateDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'startups';

  const [kpis, setKpis] = useState({ founders: 0, mentors: 0, partners: 0, investors: 0 });
  const [founders, setFounders] = useState<FounderRow[]>([]);
  const [mentors, setMentors] = useState<MentorRow[]>([]);
  const [partnerRows, setPartnerRows] = useState<PartnerDoc[]>([]);
  const [investors, setInvestors] = useState<InvestorRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      associateService.getAssociateKpis(),
      partnerService.getFounderRows(),
      associateService.getMentorRows(),
      associateService.getPartnerRows(),
      associateService.getInvestorRows(),
    ])
      .then(([k, f, m, p, i]) => {
        setKpis(k);
        setFounders(f);
        setMentors(m);
        setPartnerRows(p);
        setInvestors(i);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setTab = (t: TabId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', t);
    setSearchParams(params, { replace: false });
  };

  return (
    <div className="space-y-8 pb-10">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-2">
        <div>
          <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Ecosystem Portal</p>
          <h1 className="font-display text-5xl tracking-wide text-text leading-none">Pakistan Startup Ecosystem.</h1>
          <p className="font-serif-display italic text-muted text-lg mt-2">
            Read-only access for research, policy, and ecosystem quality monitoring.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:pb-1">
          <KpiPill value={loading ? '–' : kpis.founders} label="Founders" tooltip="Active founders on the platform" />
          <KpiPill value={loading ? '–' : kpis.mentors} label="Mentors" tooltip="Verified mentors" />
          <KpiPill value={loading ? '–' : kpis.partners} label="Partners" tooltip="Institutions with data-sharing agreements" />
          <KpiPill value={loading ? '–' : kpis.investors} label="Investors" tooltip="Investors reviewing published pitches" />
        </div>
      </div>

      {/* ─── Tab bar ─── */}
      <div className="border-b border-line">
        <nav className="-mb-px flex gap-1 overflow-x-auto" role="tablist">
          <TabButton
            id="startups"
            label="Startups"
            count={founders.length}
            active={activeTab === 'startups'}
            onClick={() => setTab('startups')}
          />
          <TabButton
            id="mentors"
            label="Mentors"
            count={mentors.length}
            active={activeTab === 'mentors'}
            onClick={() => setTab('mentors')}
          />
          <TabButton
            id="partners"
            label="Partners"
            count={partnerRows.length}
            active={activeTab === 'partners'}
            onClick={() => setTab('partners')}
          />
          <TabButton
            id="investors"
            label="Investors"
            count={investors.length}
            active={activeTab === 'investors'}
            onClick={() => setTab('investors')}
          />
        </nav>
      </div>

      {/* ─── Tab content ─── */}
      <div key={activeTab} className="animate-fade-in">
        {activeTab === 'startups' && <StartupsTab rows={founders} loading={loading} />}
        {activeTab === 'mentors' && <MentorsTab rows={mentors} loading={loading} />}
        {activeTab === 'partners' && <PartnersTab rows={partnerRows} loading={loading} />}
        {activeTab === 'investors' && <InvestorsTab rows={investors} loading={loading} />}
      </div>
    </div>
  );
};

export default AssociateDashboard;

// ──────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ──────────────────────────────────────────────────────────────────────────

const KpiPill: React.FC<{ value: number | string; label: string; tooltip: string }> = ({ value, label, tooltip }) => (
  <Tooltip content={tooltip}>
    <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
      <span className="font-bold text-chart tabular-nums">{value}</span>
      <span className="text-muted">{label}</span>
    </span>
  </Tooltip>
);

const TabButton: React.FC<{
  id: TabId;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ id, label, count, active, onClick }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
      active
        ? 'border-chart text-text'
        : 'border-transparent text-muted hover:text-text hover:border-line'
    }`}
  >
    {label}
    <span className={`ml-2 text-xs tabular-nums ${active ? 'text-chart' : 'text-muted'}`}>({count})</span>
  </button>
);

const SearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({
  value,
  onChange,
  placeholder,
}) => (
  <div className="relative flex-1 max-w-sm">
    <span className="absolute left-3 top-1/2 -translate-y-1/2">
      <SearchIcon />
    </span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2.5 rounded-card border border-line bg-shell text-text placeholder:text-muted/60 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-sm"
    />
  </div>
);

const EmptyState: React.FC<{ title: string; subtitle: string; onClear?: () => void }> = ({ title, subtitle, onClear }) => (
  <div className="bg-shell border border-line rounded-card p-12 text-center">
    <p className="font-display text-lg tracking-wide text-muted uppercase">{title}</p>
    <p className="font-serif-display italic text-muted text-sm mt-1 mb-4">{subtitle}</p>
    {onClear && (
      <button onClick={onClear} className="text-xs text-chart hover:text-chart/80 underline transition-colors">
        Clear all filters
      </button>
    )}
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Tab 1 — Startups
// ──────────────────────────────────────────────────────────────────────────

const STAGE_OPTS = [
  { value: '', label: 'All Stages' },
  { value: 'idea', label: 'Idea' },
  { value: 'mvp', label: 'MVP' },
  { value: 'growth', label: 'Growth' },
  { value: 'scale', label: 'Scale' },
];

const PHASE_OPTS = [
  { value: '', label: 'All Phases' },
  { value: '1', label: 'Phase 1' },
  { value: '2', label: 'Phase 2' },
  { value: '3', label: 'Phase 3' },
  { value: '4', label: 'Phase 4' },
  { value: '5', label: 'Phase 5' },
  { value: '6', label: 'Phase 6' },
];

const PITCH_OPTS = [
  { value: '', label: 'All Pitch Statuses' },
  { value: 'none', label: 'Not Started' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
];

const StartupsTab: React.FC<{ rows: FounderRow[]; loading: boolean }> = ({ rows, loading }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [stage, setStage] = useState('');
  const [phase, setPhase] = useState('');
  const [pitch, setPitch] = useState('');

  const industryOpts = useMemo(() => {
    const set = new Set(rows.map((r) => r.industry));
    return [{ value: '', label: 'All Industries' }, ...Array.from(set).sort().map((i) => ({ value: i, label: i }))];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => !q || r.founderName.toLowerCase().includes(q) || r.startupName.toLowerCase().includes(q))
      .filter((r) => !industry || r.industry === industry)
      .filter((r) => !stage || r.stage === stage)
      .filter((r) => !phase || r.currentPhase === Number(phase))
      .filter((r) => !pitch || r.pitchStatus === pitch);
  }, [rows, search, industry, stage, phase, pitch]);

  const anyActive = !!(search || industry || stage || phase || pitch);
  const clearAll = () => {
    setSearch('');
    setIndustry('');
    setStage('');
    setPhase('');
    setPitch('');
  };

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search founder or startup…" />
        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-44">
            <Select name="industry" options={industryOpts} value={industry} onChange={(e) => setIndustry(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="stage" options={STAGE_OPTS} value={stage} onChange={(e) => setStage(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="phase" options={PHASE_OPTS} value={phase} onChange={(e) => setPhase(e.target.value)} />
          </div>
          <div className="w-48">
            <Select name="pitch" options={PITCH_OPTS} value={pitch} onChange={(e) => setPitch(e.target.value)} />
          </div>
          {anyActive && (
            <button onClick={clearAll} className="text-xs text-muted hover:text-text underline transition-colors">
              Clear
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-muted tabular-nums">
          {loading ? '—' : `${filtered.length} of ${rows.length} startups`}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <p className="font-serif-display italic text-muted">Loading ecosystem data…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="No Matches" subtitle="Try adjusting your search or filters." onClear={anyActive ? clearAll : undefined} />
      ) : (
        <>
          <div className="hidden md:block bg-shell border border-line rounded-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line bg-surface/40">
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Founder</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Startup</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Industry / Stage</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Phase</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Coursework</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted hidden lg:table-cell">Attendance</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Pitch</th>
                    <th className="text-right py-3 px-5" aria-label="Open" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtered.map((row, i) => (
                    <tr
                      key={row.startupId}
                      onClick={() => navigate(`/app/associate/startup/${row.startupId}`)}
                      className="group hover:bg-accent-soft/50 cursor-pointer transition-colors animate-fade-in-up"
                      style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'both' }}
                    >
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs flex-shrink-0">
                            {initialsOf(row.founderName)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-text text-sm truncate">{row.founderName}</div>
                            {row.founderRegion && <div className="text-xs text-muted truncate">{row.founderRegion}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5 max-w-[260px]">
                        <div className="font-semibold text-text text-sm truncate">{row.startupName}</div>
                        <div className="text-xs text-muted truncate">{row.startupTagline}</div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex flex-wrap gap-1.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${industryColor[row.industry] || 'bg-surface text-muted border-line'}`}>
                            {row.industry}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line">
                            {stageLabel[row.stage] || row.stage}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-5 min-w-[200px]">
                        <div className="text-xs font-medium text-text">
                          Phase {row.currentPhase} <span className="text-muted font-normal">— {row.currentPhaseTitle}</span>
                        </div>
                        <div className="mt-1.5 w-full max-w-[180px] h-1 bg-surface rounded-full overflow-hidden">
                          <div className="h-full bg-chart rounded-full transition-all" style={{ width: `${row.phaseProgressPct}%` }} />
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text tabular-nums">{row.courseworkPct}%</span>
                          <div className="w-14 h-1 bg-surface rounded-full overflow-hidden">
                            <div className="h-full bg-success rounded-full" style={{ width: `${row.courseworkPct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5 hidden lg:table-cell">
                        {row.sessionsInvited > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${attendanceDot(row.sessionsAttended, row.sessionsInvited)}`} />
                            <span className="text-sm text-text tabular-nums">
                              {row.sessionsAttended}
                              <span className="text-muted">/{row.sessionsInvited}</span>
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted">—</span>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${pitchBadge(row.pitchStatus)}`}>
                          {pitchLabel[row.pitchStatus]}
                        </span>
                      </td>
                      <td className="py-4 px-5 text-right">
                        <ChevronRight className="w-4 h-4 text-muted group-hover:text-chart transition-colors inline-block" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((row, i) => (
              <button
                key={row.startupId}
                onClick={() => navigate(`/app/associate/startup/${row.startupId}`)}
                className="w-full text-left bg-shell border border-line rounded-card p-4 hover:border-chart/40 transition-colors animate-fade-in-up"
                style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs flex-shrink-0">
                    {initialsOf(row.founderName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text text-sm truncate">{row.startupName}</div>
                    <div className="text-xs text-muted truncate">{row.founderName} · {row.founderRegion}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted flex-shrink-0 mt-1" />
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${industryColor[row.industry] || 'bg-surface text-muted border-line'}`}>
                    {row.industry}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line">
                    {stageLabel[row.stage] || row.stage}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${pitchBadge(row.pitchStatus)}`}>
                    {pitchLabel[row.pitchStatus]}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Phase</div>
                    <div className="text-xs font-medium text-text">Phase {row.currentPhase} ({row.phaseProgressPct}%)</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Coursework</div>
                    <div className="text-xs font-medium text-text">{row.courseworkPct}%</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <p className="text-xs text-muted text-center italic">
        Anonymized ecosystem cohort data, available under a research data-sharing agreement.
      </p>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Tab 2 — Mentors
// ──────────────────────────────────────────────────────────────────────────

const EXPERIENCE_OPTS = [
  { value: '', label: 'Any Experience' },
  { value: '5', label: '5+ years' },
  { value: '10', label: '10+ years' },
  { value: '15', label: '15+ years' },
];

const MentorsTab: React.FC<{ rows: MentorRow[]; loading: boolean }> = ({ rows, loading }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [expertise, setExpertise] = useState('');
  const [region, setRegion] = useState('');
  const [minExp, setMinExp] = useState('');

  const expertiseOpts = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.expertise.forEach((e) => set.add(e)));
    return [{ value: '', label: 'All Expertise' }, ...Array.from(set).sort().map((e) => ({ value: e, label: e }))];
  }, [rows]);

  const regionOpts = useMemo(() => {
    const set = new Set(rows.map((r) => r.region).filter(Boolean));
    return [{ value: '', label: 'All Regions' }, ...Array.from(set).sort().map((r) => ({ value: r, label: r }))];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minExp ? parseInt(minExp, 10) : 0;
    return rows
      .filter(
        (r) =>
          !q ||
          r.name.toLowerCase().includes(q) ||
          r.company.toLowerCase().includes(q) ||
          r.expertise.some((e) => e.toLowerCase().includes(q))
      )
      .filter((r) => !expertise || r.expertise.includes(expertise))
      .filter((r) => !region || r.region === region)
      .filter((r) => r.experienceYears >= min);
  }, [rows, search, expertise, region, minExp]);

  const anyActive = !!(search || expertise || region || minExp);
  const clearAll = () => {
    setSearch('');
    setExpertise('');
    setRegion('');
    setMinExp('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search mentor, company, or expertise…" />
        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-44">
            <Select name="expertise" options={expertiseOpts} value={expertise} onChange={(e) => setExpertise(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="region" options={regionOpts} value={region} onChange={(e) => setRegion(e.target.value)} />
          </div>
          <div className="w-40">
            <Select name="minExp" options={EXPERIENCE_OPTS} value={minExp} onChange={(e) => setMinExp(e.target.value)} />
          </div>
          {anyActive && (
            <button onClick={clearAll} className="text-xs text-muted hover:text-text underline transition-colors">
              Clear
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-muted tabular-nums">
          {loading ? '—' : `${filtered.length} of ${rows.length} mentors`}
        </span>
      </div>

      {loading ? (
        <p className="font-serif-display italic text-muted">Loading mentor network…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="No Matches" subtitle="Try adjusting your search or filters." onClear={anyActive ? clearAll : undefined} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((m, i) => (
            <button
              key={m.id}
              onClick={() => navigate(`/app/associate/mentor/${m.id}`)}
              className="group text-left bg-shell border border-line rounded-card p-5 hover:border-chart/40 hover:shadow-md transition-all animate-fade-in-up"
              style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-sm flex-shrink-0">
                  {initialsOf(m.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-text text-sm truncate">{m.name}</div>
                  <div className="text-xs text-muted truncate">{m.title}</div>
                  {m.company && <div className="text-xs text-muted truncate">{m.company}</div>}
                </div>
                {m.status === 'approved' && (
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-success" title="Verified" />
                )}
              </div>

              <div className="flex items-center gap-3 text-xs text-muted mb-4">
                {m.region && <span>📍 {m.region}</span>}
                {m.experience && <span>· {m.experience}</span>}
              </div>

              {m.expertise.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {m.expertise.slice(0, 3).map((e) => (
                    <span key={e} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-chart/10 text-chart border border-chart/20">
                      {e}
                    </span>
                  ))}
                  {m.expertise.length > 3 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line">
                      +{m.expertise.length - 3}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-muted pt-3 border-t border-line">
                <span className="tabular-nums">
                  <span className="text-text font-semibold">{m.menteesCount}</span> mentees ·{' '}
                  <span className="text-text font-semibold">{m.sessionsLed}</span> sessions led
                </span>
                <ChevronRight className="w-3.5 h-3.5 group-hover:text-chart transition-colors" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Tab 3 — Partners
// ──────────────────────────────────────────────────────────────────────────

const PARTNER_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'University', label: 'University' },
  { value: 'Accelerator', label: 'Accelerator' },
  { value: 'Government', label: 'Government' },
  { value: 'Corporate', label: 'Corporate' },
];

const PartnersTab: React.FC<{ rows: PartnerDoc[]; loading: boolean }> = ({ rows, loading }) => {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [region, setRegion] = useState('');

  const regionOpts = useMemo(() => {
    const set = new Set(rows.map((r) => r.region).filter(Boolean));
    return [{ value: '', label: 'All Regions' }, ...Array.from(set).sort().map((r) => ({ value: r, label: r }))];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(
        (r) =>
          !q ||
          r.institutionName.toLowerCase().includes(q) ||
          r.primaryContact.toLowerCase().includes(q) ||
          r.location.toLowerCase().includes(q)
      )
      .filter((r) => !type || r.type === type)
      .filter((r) => !region || r.region === region);
  }, [rows, search, type, region]);

  const anyActive = !!(search || type || region);
  const clearAll = () => {
    setSearch('');
    setType('');
    setRegion('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search institution or contact…" />
        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-44">
            <Select name="type" options={PARTNER_TYPES} value={type} onChange={(e) => setType(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="region" options={regionOpts} value={region} onChange={(e) => setRegion(e.target.value)} />
          </div>
          {anyActive && (
            <button onClick={clearAll} className="text-xs text-muted hover:text-text underline transition-colors">
              Clear
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-muted tabular-nums">
          {loading ? '—' : `${filtered.length} of ${rows.length} partners`}
        </span>
      </div>

      {loading ? (
        <p className="font-serif-display italic text-muted">Loading partners…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="No Matches" subtitle="Try adjusting your search or filters." onClear={anyActive ? clearAll : undefined} />
      ) : (
        <div className="bg-shell border border-line rounded-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line bg-surface/40">
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Institution</th>
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Type</th>
                  <th className="text-right py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Students</th>
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted hidden md:table-cell">Primary Contact</th>
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted hidden lg:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((p, i) => (
                  <tr
                    key={p._id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'both' }}
                  >
                    <td className="py-4 px-5">
                      <div className="font-semibold text-text text-sm">{p.institutionName}</div>
                      <div className="text-xs text-muted flex items-center gap-1.5 mt-0.5">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                        </svg>
                        {p.location}
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${partnerTypeColor[p.type]}`}>
                        {p.type}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-right">
                      <span className="font-display text-lg text-text tabular-nums">{p.studentCount}</span>
                    </td>
                    <td className="py-4 px-5 hidden md:table-cell">
                      <div className="text-sm text-text">{p.primaryContact}</div>
                      <a href={`mailto:${p.contactEmail}`} className="text-xs text-muted hover:text-chart transition-colors truncate block">
                        {p.contactEmail}
                      </a>
                    </td>
                    <td className="py-4 px-5 text-xs text-muted hidden lg:table-cell">{formatDate(p.joinedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-muted text-center italic">
        Institution partners visible here have signed data-sharing agreements with BunyadKar.
      </p>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Tab 4 — Investors
// ──────────────────────────────────────────────────────────────────────────

const InvestorsTab: React.FC<{ rows: InvestorRow[]; loading: boolean }> = ({ rows, loading }) => {
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');

  const industryOpts = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.industry.forEach((ind) => set.add(ind)));
    return [{ value: '', label: 'All Industries' }, ...Array.from(set).sort().map((i) => ({ value: i, label: i }))];
  }, [rows]);

  const regionOpts = useMemo(() => {
    const set = new Set(rows.map((r) => r.region).filter(Boolean));
    return [{ value: '', label: 'All Regions' }, ...Array.from(set).sort().map((r) => ({ value: r, label: r }))];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.company.toLowerCase().includes(q))
      .filter((r) => !industry || r.industry.includes(industry))
      .filter((r) => !region || r.region === region);
  }, [rows, search, industry, region]);

  const anyActive = !!(search || industry || region);
  const clearAll = () => {
    setSearch('');
    setIndustry('');
    setRegion('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search investor or firm…" />
        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-44">
            <Select name="industry" options={industryOpts} value={industry} onChange={(e) => setIndustry(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="region" options={regionOpts} value={region} onChange={(e) => setRegion(e.target.value)} />
          </div>
          {anyActive && (
            <button onClick={clearAll} className="text-xs text-muted hover:text-text underline transition-colors">
              Clear
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-muted tabular-nums">
          {loading ? '—' : `${filtered.length} of ${rows.length} investors`}
        </span>
      </div>

      {loading ? (
        <p className="font-serif-display italic text-muted">Loading investor network…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="No Matches" subtitle="Try adjusting your search or filters." onClear={anyActive ? clearAll : undefined} />
      ) : (
        <div className="bg-shell border border-line rounded-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line bg-surface/40">
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Investor</th>
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted hidden sm:table-cell">Firm</th>
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted hidden md:table-cell">Region</th>
                  <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Focus</th>
                  <th className="text-right py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Pitches Reviewed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((inv, i) => (
                  <tr
                    key={inv.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'both' }}
                  >
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs flex-shrink-0">
                          {initialsOf(inv.name)}
                        </div>
                        <div>
                          <div className="font-medium text-text text-sm">{inv.name}</div>
                          <div className="text-xs text-muted">{inv.title}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-5 text-sm text-text hidden sm:table-cell">{inv.company}</td>
                    <td className="py-4 px-5 text-sm text-muted hidden md:table-cell">{inv.region}</td>
                    <td className="py-4 px-5">
                      <div className="flex flex-wrap gap-1.5">
                        {inv.industry.slice(0, 2).map((ind) => (
                          <span
                            key={ind}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${industryColor[ind] || 'bg-surface text-muted border-line'}`}
                          >
                            {ind}
                          </span>
                        ))}
                        {inv.industry.length > 2 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line">
                            +{inv.industry.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-5 text-right">
                      <span className="font-display text-lg text-text tabular-nums">{inv.pitchesReviewed}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
